import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";
import { getVendors } from "./vendorSlice";

export const getVendorDetail = createAsyncThunk(
  "VendorDetail/get",
  async (vendorId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.VENDOR.SLUG}/${vendorId}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addVendorContact = createAsyncThunk(
  "VendorContact/add",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.VENDOR.CONTACT.SLUG, payload);
      dispatch(getVendorsContact(payload.vendorId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateVendorContact = createAsyncThunk(
  "VendorContact/update",
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(API_URL.VENDOR.CONTACT.SLUG, payload);
      dispatch(getVendorsContact(payload.vendorId));
      const { active_payload } = getState().vendor;
      if (active_payload) {
        dispatch(getVendors(active_payload));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteVendorContact = createAsyncThunk(
  "VendorContact/delete",
  async ({ contactId, vendorId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(
        `${API_URL.VENDOR.CONTACT.SLUG}/${contactId}`,
      );
      dispatch(getVendorsContact(vendorId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addVendorBanking = createAsyncThunk(
  "VendorBanking/add",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.VENDOR.BANKING.SLUG, payload);
      dispatch(getVendorsBanking(payload.vendorId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const updateVendorBanking = createAsyncThunk(
  "VendorBanking/update",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(API_URL.VENDOR.BANKING.SLUG, payload);
      dispatch(getVendorsBanking(payload.vendorId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const deleteVendorBanking = createAsyncThunk(
  "VendorBanking/delete",
  async ({ bankId, vendorId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(
        `${API_URL.VENDOR.BANKING.SLUG}/${bankId}`,
      );
      dispatch(getVendorsBanking(vendorId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getVendorsContact = createAsyncThunk(
  "VendorsContact/get",
  async (vendorId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.VENDOR.CONTACT.GET}/${vendorId}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getVendorsBanking = createAsyncThunk(
  "VendorsBanking/get",
  async (vendorId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL.VENDOR.BANKING.GET}/${vendorId}`,
      );
      return response?.data?.data || [];
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message || "Failed to fetch banking details");
    }
  },
);

export const getVendorsAddress = createAsyncThunk(
  "VendorsAddress/get",
  async (vendorId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.VENDOR.ADDRESS.GET}/${vendorId}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addVendorAddress = createAsyncThunk(
  "addVendorAddress/post",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.VENDOR.ADDRESS.SLUG}`,
        payload,
      );
      dispatch(getVendorsAddress(payload.vendorId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateVendorAddress = createAsyncThunk(
  "updateVendorAddress/put",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.VENDOR.ADDRESS.SLUG}`,
        payload,
      );
      dispatch(getVendorsAddress(payload.vendorId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const vendorDetailSlice = createSlice({
  name: "vendorDetail",
  initialState: {
    vendor_detail: null,
    loading: false,
    error: null,
    vendor_contact_list: [],
    vendor_banking_list: [],
    vendor_address_list: [],
  },
  reducers: {
    resetVendorDetail(state) {
      state.vendor_detail = null;
      state.vendor_contact_list = [];
      state.vendor_banking_list = [];
      state.vendor_address_list = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getVendorDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVendorDetail.fulfilled, (state, action) => {
        state.vendor_detail = action.payload;
        state.loading = false;
      })
      .addCase(getVendorDetail.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
    builder
      .addCase(getVendorsContact.fulfilled, (state, action) => {
        state.vendor_contact_list = action.payload;
        state.loading = false;
      })
      .addCase(getVendorsContact.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(getVendorsContact.pending, (state) => {
        state.loading = true;
      });
    builder
      .addCase(getVendorsBanking.fulfilled, (state, action) => {
        state.vendor_banking_list = action.payload;
        state.loading = false;
      })
      .addCase(getVendorsBanking.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(getVendorsBanking.pending, (state) => {
        state.loading = true;
      });
    builder
      .addCase(getVendorsAddress.fulfilled, (state, action) => {
        state.vendor_address_list = action.payload;
        state.loading = false;
      })
      .addCase(getVendorsAddress.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(getVendorsAddress.pending, (state) => {
        state.loading = true;
      });
  },
});

export const { resetVendorDetail } = vendorDetailSlice.actions;
export default vendorDetailSlice.reducer;
